import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Save, Plus, Trash2, Calendar, Users, Target,
  Percent, BarChart3, GitBranch, Globe, MessageSquare,
  Clock, AlertCircle,
} from 'lucide-react';
import {
  testTypes, successMetrics, splitRatioOptions,
  confidenceLevels, audienceSegments,
} from '../data/abTests';

const typeIcons = { page: Globe, message: MessageSquare, flow: GitBranch };

export default function ExperimentForm({ test, onSave, onClose }) {
  const { t } = useTranslation();
  const isEditing = !!test;

  const [form, setForm] = useState({
    name: test?.name || '',
    type: test?.type || 'page',
    description: test?.description || '',
    hypothesis: test?.hypothesis || '',
    variantA: test?.variantA?.name || 'Control',
    variantB: test?.variantB?.name || 'Variation',
    variantAConfig: test?.variantA || {},
    variantBConfig: test?.variantB || {},
    splitRatio: test?.splitRatio || { a: 50, b: 50 },
    successMetric: test?.successMetric || 'conversion_rate',
    secondaryMetric: test?.secondaryMetric || '',
    estimatedDurationDays: test?.estimatedDurationDays || 14,
    audiencePercentage: test?.audiencePercentage || 100,
    audienceSegments: test?.audienceSegments || [],
    startDate: test?.startDate || '',
    endDate: test?.endDate || '',
    confidenceLevel: test?.confidenceLevel || 95,
    mde: test?.mde || 10, // Minimum Detectable Effect (%)
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('ab_testing.error_name_required');
    if (!form.description.trim()) e.description = t('ab_testing.error_description_required');
    if (!form.variantA?.trim()) e.variantA = t('ab_testing.error_variant_required');
    if (!form.variantB?.trim()) e.variantB = t('ab_testing.error_variant_required');
    if (form.estimatedDurationDays < 1 || form.estimatedDurationDays > 90) {
      e.duration = t('ab_testing.error_duration');
    }
    if (form.audiencePercentage < 1 || form.audiencePercentage > 100) {
      e.audience = t('ab_testing.error_audience');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const splitOpt = splitRatioOptions.find(
      o => o.a === form.splitRatio?.a && o.b === form.splitRatio?.b
    ) || splitRatioOptions[0];

    const testData = {
      name: form.name,
      type: form.type,
      description: form.description,
      hypothesis: form.hypothesis,
      variantA: { name: form.variantA, ...form.variantAConfig },
      variantB: { name: form.variantB, ...form.variantBConfig },
      splitRatio: { a: splitOpt.a, b: splitOpt.b },
      successMetric: form.successMetric,
      secondaryMetric: form.secondaryMetric || null,
      estimatedDurationDays: Number(form.estimatedDurationDays) || 14,
      audiencePercentage: Number(form.audiencePercentage) || 100,
      audienceSegments: form.audienceSegments,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      confidenceLevel: form.confidenceLevel,
      mde: form.mde,
    };

    onSave(testData);
  };

  const inputClass = 'w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-3 py-2 font-mono text-xs outline-none transition-colors';
  const labelClass = 'font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block';
  const errorClass = 'text-red-400 font-mono text-[10px] mt-1';

  const toggleSegment = (seg) => {
    setForm(prev => ({
      ...prev,
      audienceSegments: prev.audienceSegments.includes(seg)
        ? prev.audienceSegments.filter(s => s !== seg)
        : [...prev.audienceSegments, seg],
    }));
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm' onClick={onClose}>
      <div
        className='w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border border-zinc-800 shadow-2xl'
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10'>
          <h2 className='font-display text-lg uppercase'>
            {isEditing ? t('ab_testing.edit_experiment') : t('ab_testing.new_experiment')}
          </h2>
          <button onClick={onClose} className='text-zinc-500 hover:text-white transition-colors'>
            <X className='w-5 h-5' />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='p-6 space-y-6'>
          {/* Name */}
          <div>
            <label className={labelClass}>
              <Target className='w-3 h-3 inline mr-1' />
              {t('ab_testing.experiment_name')} *
            </label>
            <input
              type='text'
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={t('ab_testing.experiment_name_placeholder')}
              className={inputClass}
            />
            {errors.name && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.name}</div>}
          </div>

          {/* Type */}
          <div>
            <label className={labelClass}>
              <BarChart3 className='w-3 h-3 inline mr-1' />
              {t('ab_testing.test_type')}
            </label>
            <div className='grid grid-cols-3 gap-2'>
              {testTypes.map(tp => {
                const Icon = typeIcons[tp.value];
                const selected = form.type === tp.value;
                return (
                  <button
                    key={tp.value}
                    type='button'
                    onClick={() => setForm({ ...form, type: tp.value })}
                    className={`flex flex-col items-center gap-1.5 p-3 border transition-colors ${
                      selected
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                        : 'border-zinc-800 hover:border-zinc-700 text-zinc-400'
                    }`}
                  >
                    <Icon className='w-5 h-5' />
                    <span className='font-mono text-[10px] uppercase tracking-wider'>{tp.label}</span>
                    <span className='font-mono text-[8px] text-zinc-600 text-center leading-tight'>{tp.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>
              {t('ab_testing.description')} *
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={t('ab_testing.description_placeholder')}
              rows={2}
              className={inputClass + ' resize-none'}
            />
            {errors.description && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.description}</div>}
          </div>

          {/* Hypothesis */}
          <div>
            <label className={labelClass}>
              {t('ab_testing.hypothesis')}
            </label>
            <textarea
              value={form.hypothesis}
              onChange={e => setForm({ ...form, hypothesis: e.target.value })}
              placeholder={t('ab_testing.hypothesis_placeholder')}
              rows={2}
              className={inputClass + ' resize-none'}
            />
          </div>

          {/* Variant Names */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className={labelClass}>
                {t('ab_testing.variant_a')} *
              </label>
              <input
                type='text'
                value={form.variantA}
                onChange={e => setForm({ ...form, variantA: e.target.value })}
                className={inputClass}
              />
              {errors.variantA && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.variantA}</div>}
            </div>
            <div>
              <label className={labelClass}>
                {t('ab_testing.variant_b')} *
              </label>
              <input
                type='text'
                value={form.variantB}
                onChange={e => setForm({ ...form, variantB: e.target.value })}
                className={inputClass}
              />
              {errors.variantB && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.variantB}</div>}
            </div>
          </div>

          {/* Split Ratio */}
          <div>
            <label className={labelClass}>
              <Percent className='w-3 h-3 inline mr-1' />
              {t('ab_testing.split_ratio')}
            </label>
            <div className='grid grid-cols-4 gap-2'>
              {splitRatioOptions.map(opt => {
                const selected = form.splitRatio?.a === opt.a;
                return (
                  <button
                    key={opt.label}
                    type='button'
                    onClick={() => setForm({ ...form, splitRatio: { a: opt.a, b: opt.b } })}
                    className={`p-2 border text-center transition-colors ${
                      selected
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                        : 'border-zinc-800 hover:border-zinc-700 text-zinc-400'
                    }`}
                  >
                    <div className='font-mono text-xs font-bold'>{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Metrics */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className={labelClass}>
                <Target className='w-3 h-3 inline mr-1' />
                {t('ab_testing.primary_metric')}
              </label>
              <select
                value={form.successMetric}
                onChange={e => setForm({ ...form, successMetric: e.target.value })}
                className={inputClass}
              >
                {successMetrics.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                <BarChart3 className='w-3 h-3 inline mr-1' />
                {t('ab_testing.secondary_metric')}
              </label>
              <select
                value={form.secondaryMetric}
                onChange={e => setForm({ ...form, secondaryMetric: e.target.value })}
                className={inputClass}
              >
                <option value=''>{t('ab_testing.none')}</option>
                {successMetrics.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Duration & Confidence */}
          <div className='grid grid-cols-3 gap-4'>
            <div>
              <label className={labelClass}>
                <Clock className='w-3 h-3 inline mr-1' />
                {t('ab_testing.duration_days')}
              </label>
              <input
                type='number'
                min={1}
                max={90}
                value={form.estimatedDurationDays}
                onChange={e => setForm({ ...form, estimatedDurationDays: e.target.value })}
                className={inputClass}
              />
              {errors.duration && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.duration}</div>}
            </div>
            <div>
              <label className={labelClass}>
                {t('ab_testing.confidence_level')}
              </label>
              <select
                value={form.confidenceLevel}
                onChange={e => setForm({ ...form, confidenceLevel: Number(e.target.value) })}
                className={inputClass}
              >
                {confidenceLevels.map(cl => (
                  <option key={cl.value} value={cl.value}>{cl.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                {t('ab_testing.mde')}
              </label>
              <input
                type='number'
                min={1}
                max={100}
                value={form.mde}
                onChange={e => setForm({ ...form, mde: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          {/* Audience Targeting */}
          <div>
            <label className={labelClass}>
              <Users className='w-3 h-3 inline mr-1' />
              {t('ab_testing.audience_targeting')}
            </label>
            <div className='space-y-3'>
              <div>
                <div className='flex items-center gap-3'>
                  <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-24'>
                    {t('ab_testing.traffic_percentage')}
                  </span>
                  <input
                    type='range'
                    min={1}
                    max={100}
                    value={form.audiencePercentage}
                    onChange={e => setForm({ ...form, audiencePercentage: Number(e.target.value) })}
                    className='flex-1 accent-amber-500'
                  />
                  <span className='font-mono text-xs text-amber-400 w-12 text-right'>{form.audiencePercentage}%</span>
                </div>
                {errors.audience && <div className={errorClass}><AlertCircle className='w-3 h-3 inline mr-1' />{errors.audience}</div>}
              </div>
              <div>
                <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block'>
                  {t('ab_testing.segments')}
                </span>
                <div className='flex flex-wrap gap-2'>
                  {audienceSegments.map(seg => {
                    const selected = form.audienceSegments.includes(seg.value);
                    return (
                      <button
                        key={seg.value}
                        type='button'
                        onClick={() => toggleSegment(seg.value)}
                        className={`px-3 py-1 border font-mono text-[10px] uppercase tracking-wider transition-colors ${
                          selected
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-zinc-800 hover:border-zinc-700 text-zinc-500'
                        }`}
                      >
                        {seg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Scheduling */}
          <div>
            <label className={labelClass}>
              <Calendar className='w-3 h-3 inline mr-1' />
              {t('ab_testing.scheduling')}
            </label>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <span className='font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1 block'>
                  {t('ab_testing.start_date')}
                </span>
                <input
                  type='date'
                  value={form.startDate}
                  onChange={e => setForm({ ...form, startDate: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <span className='font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-1 block'>
                  {t('ab_testing.end_date')}
                </span>
                <input
                  type='date'
                  value={form.endDate}
                  onChange={e => setForm({ ...form, endDate: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className='flex items-center justify-end gap-3 pt-2 border-t border-zinc-800'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 border border-zinc-700 hover:border-zinc-600 text-zinc-400 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors'
            >
              {t('common.cancel_anytime')}
            </button>
            <button
              type='submit'
              className='flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-2 font-bold uppercase tracking-wider text-xs transition-colors'
            >
              <Save className='w-3.5 h-3.5' />
              {isEditing ? t('ab_testing.save_changes') : t('ab_testing.create_experiment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
